import React, { useRef, useState, useEffect } from "react";
import { Input, Row, Col, FormFeedback, FormGroup, Label, FormText, Tooltip, ModalHeader, ModalFooter, ModalBody, Alert, InputGroup } from "reactstrap";
import CustomSelect from "../../../components/CustomSelect";
import { computeCondition } from "./moduleBuilderFunctions";
import { useLockState } from "../../../components/userLocks/LockCheck";

function ModuleInputControl({inputGuid, inputs, values, isOnVisiblePage = true, processedRuleResultsMap, handleInputValueChanged, handleInputValidityChanged, handleInputWarningsChanged}) {
    const [showWarningTooltip, setShowWarningTooltip] = useState(false);
    const [warningMessage, setWarningMessage] = useState("");
    const [inputIsValid, setInputIsValid] = useState(false)
    const tooltipTimeoutRef = useRef(null); // Ref for managing the timeout of the tooltip
    const [warningMessages, setWarningMessages] = useState([]);
    const [errorMessages, setErrorMessages] = useState([]);
    const [isVisible, setIsVisible] = useState(true);
    const [isEnabled, setIsEnabled] = useState(true);
    const lockState = useLockState();

    const input = inputs?.find(x => x.guid === inputGuid)
    const value = values?.find(x => x.inputGuid === input?.guid)?.value

    useEffect(() => {
        checkInputValidityAgainstLimits()
        applyInputControlRules()
        addUserFeedbackBasedOnInputChecks()
    }, []);

    useEffect(() => {
        addUserFeedbackBasedOnInputChecks()
        applyInputControlRules()
    }, [processedRuleResultsMap, values, isOnVisiblePage]);

    useEffect(() => {
        checkInputValidityAgainstLimits()
    }, [value, isOnVisiblePage]);

    useEffect(() => {
        handleInputValidityChanged?.(input.guid, !isVisible || !isEnabled || !isOnVisiblePage || (inputIsValid && errorMessages.length === 0))
    }, [isVisible, isEnabled, inputIsValid, errorMessages, isOnVisiblePage]);

    useEffect(() => {
        if (!handleInputWarningsChanged) return;

        if (isVisible && isOnVisiblePage && isEnabled) {
            handleInputWarningsChanged?.(input.guid, warningMessages)
        } else {
            handleInputWarningsChanged?.(input.guid, [])
        }
    }, [isVisible, isEnabled, warningMessages, isOnVisiblePage]);

    const onSelectInputValueChange = (selectedOption, ActionMeta) => {
        handleInputValueChanged(input.guid, selectedOption.value)
    }

    const displayWarningTooltip = (message) => {
        setWarningMessage(message);
        setShowWarningTooltip(true);

        // Clear the previous timeout to debounce the hiding of the tooltip
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }

        // Set the new timeout to hide the tooltip after the desired delay
        tooltipTimeoutRef.current = setTimeout(() => {
            if (inputIsValid) {
                // Temporary message (like pressing invalid key), should only hide the tooltip and clear the message, if the input is now valid
                setShowWarningTooltip(false);
                setWarningMessage("");

                // Check again to see if the input is still valid. Show the problem with it if not
                checkInputValidityAgainstLimits()
            }
        }, 2000 + (50 * message.length));  // Adjust delay based on message length
    };

    const onInputValueChange = (e) => {
        if (input.type.toLowerCase() == "number") {
            let inputValue = e.target.value;

            // Remove invalid characters (anything that's not digit, dot, or minus)
            var valueStrippedOfInvalidChars = inputValue.replace(/[^0-9.-]/g, '');

            if (valueStrippedOfInvalidChars !== inputValue) {
                displayWarningTooltip("Only numeric characters are supported");
                inputValue = valueStrippedOfInvalidChars;
            }
          
            // Ensure only one minus sign at the beginning
            if (inputValue.includes('-')) {
                if (inputValue.split('-').length > 2) {
                    displayWarningTooltip("You can't enter more than one negative symbol");
                } else if (inputValue.split('-')[0] !== "") {
                    displayWarningTooltip("The negative symbol can only be at the start of the value");
                }

                var inputWithNegativeSymbolRemoved = inputValue.replace(/-/g, '');

                if (inputValue.charAt(0) === '-') {
                    inputValue = '-' + inputWithNegativeSymbolRemoved
                } else {
                    inputValue = inputWithNegativeSymbolRemoved
                }
            }

            var isNegative = false;

            if (inputValue.length > 0 && inputValue.charAt(0) === "-") {
                // Remove the leading negative symbol if there is one, and make a note that this number is negative.
                // We need to remove it, because later we are going to take all characters before the decimal point
                // and truncate to the first 12.  We only want the numbers when we do this.
                isNegative = true;
                inputValue = inputValue.slice(1)
            }

            if (inputValue.length > 0 && inputValue.charAt(0) === ".") {
                // If they start with a ".", then we insert a 0 before it
                inputValue = "0" + inputValue
            }
          
            // Ensure only one decimal point
            const parts = inputValue.split('.');

            var valueBeforeDecimalPoint = parts[0];
            
            if (valueBeforeDecimalPoint.length > 12) {
                // 12 digits max before decimal point
                valueBeforeDecimalPoint = valueBeforeDecimalPoint.slice(0, 12)
                displayWarningTooltip("12 Digits max for numeric values")
            }

            if (valueBeforeDecimalPoint === "" && parts.length > 1) {
                // If they started the input with a ".", then we automatically insert a "0" before it
                valueBeforeDecimalPoint = "0"
            }

            if (input.decimalPlaces === 0) {
                // Discard any decimal places that the user entered, as this input doesn't support it
                inputValue = valueBeforeDecimalPoint

                if (parts.length > 1) {
                    // Entered a decimal place, when none is supported
                    displayWarningTooltip("This input only supports whole numbers")
                }
            } else if (parts.length > 1) {
                // At least one decimal point has been entered.  We need to take all digits after the first decimal
                // place and ignore any additional numbers after any additional decimal points, then truncate it to the
                // number of decimal places that this input supports.
                let decimalPortionToRequiredNumDecimalPlaces = parts[1];

                if (parts.length > 2) {
                    displayWarningTooltip("You already entered a decimal point");
                }
                
                if (decimalPortionToRequiredNumDecimalPlaces.length > input.decimalPlaces) {
                    decimalPortionToRequiredNumDecimalPlaces = decimalPortionToRequiredNumDecimalPlaces.slice(0,input.decimalPlaces)
                    displayWarningTooltip("Only " + input.decimalPlaces + " decimal places are supported");
                }
                inputValue = valueBeforeDecimalPoint + '.' + decimalPortionToRequiredNumDecimalPlaces;
            } else if (parts.length === 1) {
                // User didn't enter a number with decimal portion
                inputValue = valueBeforeDecimalPoint
            }

            if (isNegative) {
                inputValue = "-" + inputValue;
            }

            handleInputValueChanged(input.guid, inputValue);
        } else if (input.type.toLowerCase() === "text") {
            let inputValue = e.target.value;

            if (input.maxLength !== undefined && input.maxLength !== "" && inputValue.length > input.maxLength) {
                displayWarningTooltip("Max length allowed is " + input.maxLength + " characters");
                inputValue = inputValue.slice(0, input.maxLength)
            }

            handleInputValueChanged(input.guid, inputValue)
        } else if (input.type.toLowerCase() === "checkbox") {
            let inputValue = e.target.checked;
            handleInputValueChanged(input.guid, inputValue)
        } else {
            handleInputValueChanged(input.guid, e.target.value)
        }
    }

    const onInputBlur = (e) => {
        // On losing focus, we need to tidy up for some edge cases that we couldn't correct when they were typing, as that would have
        // stopped them inputting sequences that start off invalid, but become valid as you enter more characters

        if (input.type.toLowerCase() == "number") {
            let inputValue = e.target.value;

            if (inputValue === "" || inputValue === "-" || inputValue === "-0") {
                // Edge cases, that we will treat as zero.
                inputValue = "0"
            }

            var isNegative = false;

            if (inputValue.length > 0 && inputValue.charAt(0) === "-") {
                // Remove the leading negative symbol if there is one, and make a note that this number is marked negative.
                // We need to remove it for now, before we do some checks that it's valid to keep this symbol.  We may add it back later
                isNegative = true;
                inputValue = inputValue.slice(1)
            }
        
            const parts = inputValue.split('.');

            var valueBeforeDecimalPoint = parts[0]
            var decimalPortionToRequiredNumDecimalPlaces = ""

            if (input.decimalPlaces === 0) {
                inputValue = valueBeforeDecimalPoint
            } else if (parts.length > 1) {
                decimalPortionToRequiredNumDecimalPlaces = (parts.slice(1).join('')).slice(0,input.decimalPlaces)

                if (input.decimalPlaces <= 4) {
                    // For shorter numbers of decimal places, we will pad the end with zeros to make it clear to the user, they could specify more precise number
                    decimalPortionToRequiredNumDecimalPlaces = decimalPortionToRequiredNumDecimalPlaces.padEnd(input.decimalPlaces, '0')
                }

                if (decimalPortionToRequiredNumDecimalPlaces !== "") {
                    inputValue = valueBeforeDecimalPoint+ '.' + decimalPortionToRequiredNumDecimalPlaces;
                } else {
                    // Value ended with a ".", and no numbers following it.  So no need to show the decimal
                    inputValue = valueBeforeDecimalPoint
                }
            } else {
                // No Decimal Point was entered
                inputValue = valueBeforeDecimalPoint

                if (input.decimalPlaces <= 4) {
                    // For shorter numbers of decimal places, we will pad the end with zeros to make it clear to the user, they could specify more precise number
                    inputValue = inputValue + '.' + '0'.repeat(input.decimalPlaces)
                }
            }

            /*
                Remove leading zeros
                ---------------------

                ^(-?) – optionally matches and captures a negative sign

                0+ – matches one or more leading zeros

                (?=\d) – only replaces if a digit comes next (so it avoids removing the 0 in 0.123)

                Examples:
                "000123" = "123"
                "0123.45" = "123.45"
                "0.001" = "0.001"
                "0000.005" = "0.005"
                "-0004.5" = "-4.5"
                "-0.25" = "-0.25"
                "0"	= "0"
            */
            inputValue = inputValue.replace(/^(-?)0+(?=\d)/, '$1'); 

            if (isNegative && (Number(valueBeforeDecimalPoint) !==0 || Number(decimalPortionToRequiredNumDecimalPlaces) !== 0)) {
                // We can put back the negative symbol, as long as the inputed number is non-zero
                inputValue = "-" + inputValue;
            }

            if (isNaN(Number(inputValue)) || inputValue === "-") {
                // Edge cases like "-." and "" are invalid.  So replace with 0
                handleInputValueChanged(input.guid, "0");
            } else {
                handleInputValueChanged(input.guid, inputValue);
            }            
        }
    }

    const applyInputControlRules = () => {
        let shouldHide = false;
        let shouldDisable = false;

        input?.control?.rules?.forEach(rule => {

            // If any conditions pass, then we should apply the rule
            rule.criteria.conditions.forEach(condition => {
                if (computeCondition(condition, values, inputs, processedRuleResultsMap)) {
                    if (rule.action === "Hide") {
                        shouldHide = true
                    }

                    if (rule.action === "Disable") {
                        shouldDisable = true
                    }
                }
            });
        });

        setIsVisible(!(shouldHide))
        setIsEnabled(!(shouldDisable))
    }

    const arraysEqual = (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    };

    const addUserFeedbackBasedOnInputChecks = () => {
        let errorList = []
        let warningList = []

        input?.validation?.checks?.forEach(check => {
            let shouldShow = true;

            // If any conditions fail, then we don't want to show the message
            check.criteria.conditions.forEach(condition => {
                if (!computeCondition(condition, values, inputs, processedRuleResultsMap)) {
                    shouldShow = false
                }
            });

            if (check.failureSeverity === "Error" && shouldShow) {
                errorList.push({guid: check.guid, message: check.failMessage})
            } else if (check.failureSeverity == "Warning" && shouldShow) {
                warningList.push({guid: check.guid, message: check.failMessage})
            }
        });

        // Only update if messages have changed
        setErrorMessages(prev =>
            !arraysEqual(prev, errorList) ? errorList : prev
        );

        setWarningMessages(prev =>
            !arraysEqual(prev, warningList) ? warningList : prev
        );
    }

    const checkInputValidityAgainstLimits = () => {
        var errorMessage = ""
        switch(input.type.toLowerCase()) {
            case "number":
                if (value === "" || (input.required && value === "0")) {
                    errorMessage = "Value is required"
                } else if (value === "-") {
                    errorMessage = "" // They need to add digits after the minus sign, but may confuse them if we give error at this stage
                } else 
                {
                    const numericValue = parseFloat(value);
                    if (value === undefined || isNaN(numericValue)) {
                            errorMessage = "Please enter a valid number";
                    } else if (input.minValue !== undefined && input.minValue !== "" && numericValue < input.minValue) {
                        if (input.maxValue === undefined || input.maxValue === "") {
                            errorMessage = `Please enter a value of ${input.minValue} or greater`;
                        } else {
                            errorMessage = `Please enter a value between ${input.minValue} and ${input.maxValue}`;
                        }
                    } else if (input.maxValue !== undefined && input.maxValue !== "" && numericValue > input.maxValue) {
                        if (input.minValue === undefined || input.minValue === "") {
                            errorMessage = `Please enter a value of ${input.maxValue} or less`;  
                        } else {
                            errorMessage = `Please enter a value between ${input.minValue} and ${input.maxValue}`;  
                        }
                    }  
                }
                break;
            case "text":
                if ((value === undefined || value === "" || value === null) && input.required) {
                    errorMessage = "Value is required";
                } else {
                    if (input.minLength !== undefined && input.minLength !== "" && (value?.length ?? 0) < input.minLength) {
                        if (input.maxLength === undefined || input.maxLength === "") {
                            errorMessage = `Please enter at least ${input.minLength} characters`;
                        } else {
                            errorMessage = `Please enter between ${input.minLength} and ${input.maxLength} characters`;
                        }
                    } else if (input.maxLength !== undefined && input.maxLength !== "" && (value?.length ?? 0) > input.maxLength) {
                        if (input.minLength === undefined || input.minLength === "") {
                            errorMessage = `Please enter no more than ${input.maxLength} characters`;
                        } else {
                            errorMessage = `Please enter between ${input.minLength} and ${input.maxLength} characters`;
                        }
                    }
                }
                break;
            case "select":
            case "radio":
                if (value === null && input.required) {
                    errorMessage = "Value is required";
                }
                break;
        }

        setWarningMessage(errorMessage);

        if (errorMessage !== "") {
            setShowWarningTooltip(true)
        }

        var newIsValidValue = (errorMessage === "")

        setInputIsValid(prev => (prev !== newIsValidValue ? newIsValidValue : prev));
    };

    if (!isVisible) return;

    return (
        <div>
            <FormGroup className={lockState?.isLocked ? "locked" : ""} style={{ flexGrow: 1, margin: "0 1rem", position: "relative"}}>
                {input.label && (
                    <Row>
                        <Col>
                            <Label htmlFor={input.name} className="ms-1 me-3">
                                {input.label} {input.required && "*"}
                            </Label>
                        </Col>
                    </Row>
                )}
                    {input.type === "select" ? (
                        <Row>
                            <Col sm={
                                input.size === "xs"
                                    ? 2
                                    : input.size === "sm"
                                    ? 4
                                    : input.size === "md"
                                        ? 6
                                        : input.size === "lg"
                                            ? 12
                                            : 12
                            }>
                                <Input type="hidden" id={input.guid} name={input.name} disabled />
                                <CustomSelect
                                    id={input.guid}
                                    name={input.name}
                                    options={input.options}
                                    isSearchable={input.isSearchable}
                                    sortItems={false}
                                    value={value ?? null}
                                    onChange={onSelectInputValueChange}
                                    disabled={!isEnabled}
                                />
                            </Col>
                        </Row>
                    ) : input.type === "radio" ? (
                        <div>
                            {input.options.map((option, idx) => (
                                <FormGroup check className="me-3" key={idx}>
                                    <Input
                                        id={`${input.guid}-${option.value}`}
                                        type="radio"
                                        name={input.name}
                                        value={option.value}
                                        checked={value === option.value}
                                        onChange={onInputValueChange}
                                        disabled={!isEnabled}
                                    />
                                    <Label check htmlFor={`${input.guid}-${option.value}`} className="ms-1 me-3">
                                        {option.label}
                                    </Label>
                                </FormGroup>
                            ))}
                        </div>
                    ) : input.type === "checkbox" ? (
                        <Row>
                            <Col sm={
                                input.size === "xs"
                                    ? 2
                                    : input.size === "sm"
                                        ? 4
                                        : input.size === "md"
                                            ? 6
                                            : input.size === "lg"
                                                ? 10
                                                : 12
                            }>
                                    <Input
                                        id={input.guid}
                                        type="checkbox"
                                        name={input.name}
                                        checked={!!value}
                                        onChange={onInputValueChange}
                                        disabled={!isEnabled}
                                    />
                            </Col>
                        </Row>
                    ): (
                        <Row>
                            <Col sm={
                                input.size === "xs"
                                    ? 2
                                    : input.size === "sm"
                                        ? 4
                                        : input.size === "md"
                                            ? 6
                                            : input.size === "lg"
                                                ? 10
                                                : 12
                            }>
                                <Input
                                    id={input.guid}
                                    name={input.name}
                                    
                                    /* To avoid the up/down arrows, we enter numerics as if they were text. Otherwise we would have to do CSS
                                       to remove them.  Using text input also gives us more flexibility when inputting decimal numbers, and restricting input */
                                    type={input.type.toLowerCase() === "number" ? "text" : input.type} 

                                    placeholder={input.placeholder}
                                    required={input.required}
                                    value={value ?? ""}
                                    disabled={input.readonly || !isEnabled}
                                    onChange={onInputValueChange}
                                    onBlur={onInputBlur}
                                    invalid={warningMessage !== ""}
                                    autoComplete="off"
                                />                                
                            </Col>
                        </Row>
                    )}
                    { input.help && (
                        <Row>
                            <Col>
                                <FormText><i className="bi bi-info-circle-fill me-2"></i>{input.help}</FormText>
                            
                            </Col>
                        </Row>
                    )}
                    {showWarningTooltip && warningMessage !== "" &&
                            <Row>
                                <Col>
                                    <FormText><i className="text-danger bi bi-x-circle me-2"></i>{warningMessage}</FormText>                                
                                </Col>
                            </Row>
                    }
                    {
                        isEnabled &&
                        errorMessages.length > 0 &&
                        errorMessages.map(error => 
                            <Row key={error.guid}>
                                <Col>
                                    <FormText><i className="text-danger bi bi-x-circle me-2"></i>{error.message}</FormText>                                
                                </Col>
                            </Row>
                        )
                    }
                    {
                        isEnabled &&
                        warningMessages.length > 0 &&
                        warningMessages.map(warning => 
                            <Row key={warning.guid}>
                                <Col>
                                    <FormText><i className="text-warning bi bi-exclamation-triangle me-2"></i>{warning.message}</FormText>                                
                                </Col>
                            </Row>
                        )
                    }
            </FormGroup>
        </div>
    )
}
export default ModuleInputControl;